import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Share2, Brain, Filter, ChevronLeft, ChevronRight, Globe, Lock, FolderOpen, RefreshCw } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import type { Fiche, Quiz } from '../lib/types';
import { matiereColors, defaultMatiereColor, getColorForMatiere as getColorForMatiereUtil } from '../lib/matiereColors';

const ITEMS_PER_PAGE = 10;

const getRegularMatieres = (niveau: string) => {
  const baseRegularMatieres = [
    'Allemand',
    'Anglais',
    'Arts plastiques',
    'Enseignement scientifique',
    'Espagnol',
    'Français',
    'Histoire-Géo-EMC',
    'Musique',
    'Philosophie',
    'Physique-Chimie',
    'Sciences Numériques et Technologie',
    'SVT (Sciences de la Vie et de la Terre)',
    'Technologie'
  ];

  if (!niveau || ['6ème', '5ème', '4ème', '3ème', 'Seconde'].includes(niveau)) {
    baseRegularMatieres.push('Mathématiques');
  }
  if (niveau === 'Seconde') {
    baseRegularMatieres.push('SES (Sciences Économiques et Sociales)');
  }

  return baseRegularMatieres.sort();
};

const getSpecialtyMatieres = (niveau: string) => {
  const baseSpecialtyMatieres = [
    'Éduc. Médias et Information (EMI)',
    'Hist. Géo, Géopolitique et Sciences Pol.',
    'Humanités, Littérature et Philosophie',
    'Langues, Littératures et Cultures Étrangères',
    'Numérique et Sciences Informatiques',
    'Sciences de l\'ingénieur'
  ];

  if (['Première', 'Terminale'].includes(niveau)) {
    baseSpecialtyMatieres.push('Mathématiques');
    baseSpecialtyMatieres.push('SES (Sciences Économiques et Sociales)');
  }

  return baseSpecialtyMatieres.sort();
};

const optionalMatieres = [
  'Grec',
  'Latin'
].sort();

const niveaux = {
  'Collège': ['6ème', '5ème', '4ème', '3ème'],
  'Lycée': ['Seconde', 'Première', 'Terminale']
};

const isSubjectAvailable = (matiere: string, niveau: string): boolean => {
  if (!niveau) return true;

  if (matiere === 'Technologie' && 
      !['6ème', '5ème', '4ème', '3ème'].includes(niveau)) {
    return false;
  }

  if (niveau === '6ème' && ['Latin', 'Grec'].includes(matiere)) {
    return false;
  }

  if (matiere === 'Sciences Numériques et Technologie' && niveau !== 'Seconde') {
    return false;
  }

  if (matiere === 'SES (Sciences Économiques et Sociales)') {
    if (!['Seconde', 'Première', 'Terminale'].includes(niveau)) {
      return false;
    }
    if (['Première', 'Terminale'].includes(niveau)) {
      return getSpecialtyMatieres(niveau).includes(matiere);
    }
    return getRegularMatieres(niveau).includes(matiere);
  }

  if (matiere === 'Mathématiques') {
    if (['Première', 'Terminale'].includes(niveau)) {
      return getSpecialtyMatieres(niveau).includes(matiere);
    }
    return getRegularMatieres(niveau).includes(matiere);
  }

  if (matiere === 'Enseignement scientifique' && 
      !['Première', 'Terminale'].includes(niveau)) {
    return false;
  }

  const specialtyMatieres = [
    'Hist. Géo, Géopolitique et Sciences Pol.',
    'Éduc. Médias et Information (EMI)',
    'Humanités, Littérature et Philosophie',
    'Langues, Littératures et Cultures Étrangères',
    'Numérique et Sciences Informatiques',
    'Sciences de l\'ingénieur'
  ];
  if (specialtyMatieres.includes(matiere) && 
      !['Première', 'Terminale'].includes(niveau)) {
    return false;
  }

  if (matiere === 'Philosophie' && niveau !== 'Terminale') {
    return false;
  }

  return true;
};

const getColorForMatiere = getColorForMatiereUtil;

interface FiltersState {
  niveau: string;
  matiere: string;
  withQuiz: boolean;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center space-x-2 mt-4">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="text-sm text-gray-700">
        Page {currentPage} sur {totalPages}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

interface FichesListProps {
  fiches: Fiche[];
  isShared?: boolean;
  onToggleVisibility?: (ficheId: number, isPublic: boolean) => Promise<void>;
  isUpdatingVisibility?: number | null;
}

const FichesList: React.FC<FichesListProps> = ({ 
  fiches, 
  isShared = false, 
  onToggleVisibility,
  isUpdatingVisibility 
}) => {
  const [quizIds, setQuizIds] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    const fetchQuizIds = async () => {
      const ficheIds = fiches.filter(f => f.quiz).map(f => f.id);
      if (ficheIds.length === 0) return;

      try {
        const { data, error } = await supabase
          .from('quiz')
          .select('id, fiche_id')
          .in('fiche_id', ficheIds);

        if (error) throw error;

        const quizMap = data.reduce((acc, quiz) => ({
          ...acc,
          [quiz.fiche_id]: quiz.id
        }), {});

        setQuizIds(quizMap);
      } catch (err) {
        console.error('Error fetching quiz IDs:', err);
      }
    };

    fetchQuizIds();
  }, [fiches]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {fiches.map((fiche) => {
        const colors = getColorForMatiere(fiche.matiere);
        return (
          <div key={fiche.id} className={`block rounded-xl shadow-sm border ${colors.card} ${colors.border} overflow-hidden transition-transform hover:scale-[1.01] hover:shadow-md`}>
            <Link
              to={`/fiche/${fiche.id}`}
              className="block p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-bold ${colors.title} leading-snug`}>
                    {fiche.Title || fiche.sujet}
                  </p>
                  <p className={`text-xs mt-1 ${colors.sub}`}>Niveau : {fiche.niveau}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!isShared && onToggleVisibility && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        onToggleVisibility(fiche.id, !!fiche.public);
                      }}
                      className={`${colors.icon} hover:opacity-70 transition-opacity ${
                        isUpdatingVisibility === fiche.id ? 'opacity-40 cursor-wait' : ''
                      }`}
                      disabled={isUpdatingVisibility === fiche.id}
                    >
                      {fiche.public ? (
                        <Globe className="h-4 w-4" />
                      ) : (
                        <Lock className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${colors.badge} ${colors.badgeText}`}>
                    {fiche.matiere}
                  </span>
                </div>
              </div>
              {fiche.quiz && quizIds[fiche.id] && (
                <div className="mt-3">
                  <Link
                    to={`/quiz/${quizIds[fiche.id]}`}
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${colors.quizBtn}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Brain className="h-3 w-3 mr-1" />
                    Quiz disponible
                  </Link>
                </div>
              )}
            </Link>
          </div>
        );
      })}
    </div>
  );
};

const MesFichesPage = () => {
  const { user } = useAuth();
  const [ownFiches, setOwnFiches] = useState<Fiche[]>([]);
  const [communityFiches, setCommunityFiches] = useState<Fiche[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FiltersState>({
    niveau: '',
    matiere: '',
    withQuiz: false
  });
  const [ownFichesPage, setOwnFichesPage] = useState(1);
  const [communityFichesPage, setCommunityFichesPage] = useState(1);
  const [totalOwnFiches, setTotalOwnFiches] = useState(0);
  const [totalCommunityFiches, setTotalCommunityFiches] = useState(0);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchFiches = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);

      let query = supabase
        .from('Fiches')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id);

      if (filters.niveau) {
        query = query.eq('niveau', filters.niveau);
      }
      if (filters.matiere) {
        query = query.eq('matiere', filters.matiere);
      }
      if (filters.withQuiz) {
        query = query.eq('quiz', true);
      }

      query = query
        .order('created_at', { ascending: false })
        .range(
          (ownFichesPage - 1) * ITEMS_PER_PAGE,
          ownFichesPage * ITEMS_PER_PAGE - 1
        );

      const { data: ownData, error: ownError, count: ownCount } = await query;
      
      if (ownError) {
        console.error('Error fetching own fiches:', ownError);
        throw new Error('Erreur lors du chargement de vos fiches');
      }

      setOwnFiches(ownData || []);
      setTotalOwnFiches(ownCount || 0);

      let communityQuery = supabase
        .from('Fiches')
        .select('*', { count: 'exact' })
        .eq('public', true)
        .neq('user_id', user.id);

      if (filters.niveau) {
        communityQuery = communityQuery.eq('niveau', filters.niveau);
      }
      if (filters.matiere) {
        communityQuery = communityQuery.eq('matiere', filters.matiere);
      }
      if (filters.withQuiz) {
        communityQuery = communityQuery.eq('quiz', true);
      }

      communityQuery = communityQuery
        .order('created_at', { ascending: false })
        .range(
          (communityFichesPage - 1) * ITEMS_PER_PAGE,
          communityFichesPage * ITEMS_PER_PAGE - 1
        );

      const { data: communityData, error: communityError, count: communityCount } = await communityQuery;
      
      if (communityError) {
        console.error('Error fetching community fiches:', communityError);
        throw new Error('Erreur lors du chargement des fiches de la communauté');
      }

      setCommunityFiches(communityData || []);
      setTotalCommunityFiches(communityCount || 0);

    } catch (err) {
      console.error('Error in fetchFiches:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors du chargement des fiches');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchFiches();
  };

  const handleToggleVisibility = async (ficheId: number, isPublic: boolean) => {
    if (!user) return;
    
    setIsUpdatingVisibility(ficheId);
    setError(null);

    try {
      // First update the fiche visibility
      const { error: ficheError } = await supabase
        .from('Fiches')
        .update({ public: !isPublic })
        .eq('id', ficheId)
        .eq('user_id', user.id);

      if (ficheError) throw ficheError;

      // Then check if there's an associated quiz
      const { data: quizData, error: quizError } = await supabase
        .from('quiz')
        .select('id')
        .eq('fiche_id', ficheId)
        .maybeSingle();

      // Only throw error if it's not a "no rows" error
      if (quizError && !quizError.message.includes('no rows')) {
        throw quizError;
      }

      // If there's a quiz, update its visibility
      if (quizData?.id) {
        const { error: quizUpdateError } = await supabase
          .from('quiz')
          .update({ is_public: !isPublic })
          .eq('id', quizData.id)
          .eq('user_id', user.id);

        if (quizUpdateError) throw quizUpdateError;
      }

      // Update local state
      setOwnFiches(fiches =>
        fiches.map(fiche =>
          fiche.id === ficheId
            ? { ...fiche, public: !isPublic }
            : fiche
        )
      );
    } catch (err) {
      console.error('Error toggling visibility:', err);
      setError('Erreur lors de la modification de la visibilité de la fiche');
    } finally {
      setIsUpdatingVisibility(null);
    }
  };

  useEffect(() => {
    fetchFiches();
  }, [user?.id, filters, ownFichesPage, communityFichesPage]);

  const totalOwnPages = Math.ceil(totalOwnFiches / ITEMS_PER_PAGE);
  const totalCommunityPages = Math.ceil(totalCommunityFiches / ITEMS_PER_PAGE);

  const availableRegularMatieres = getRegularMatieres(filters.niveau).filter(matiere => 
    isSubjectAvailable(matiere, filters.niveau)
  );
  const availableSpecialtyMatieres = getSpecialtyMatieres(filters.niveau).filter(matiere => 
    isSubjectAvailable(matiere, filters.niveau)
  );
  const availableOptionalMatieres = optionalMatieres.filter(matiere => 
    isSubjectAvailable(matiere, filters.niveau)
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-gray-900">Mes fiches de révision</h1>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`inline-flex items-center justify-center p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition-colors ${isRefreshing ? 'animate-spin' : ''}`}
            title="Actualiser"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
        <Link
          to="/nouvelle-fiche"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-600 hover:bg-secondary-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle fiche
        </Link>
      </div>

      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center">
            <Filter className="h-4 w-4 text-gray-400 mr-2" />
            <select
              id="niveau"
              value={filters.niveau}
              onChange={(e) => {
                const newNiveau = e.target.value;
                if (!isSubjectAvailable(filters.matiere, newNiveau)) {
                  setFilters({ ...filters, niveau: newNiveau, matiere: '' });
                } else {
                  setFilters({ ...filters, niveau: newNiveau });
                }
                setOwnFichesPage(1);
                setCommunityFichesPage(1);
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-secondary-500 focus:ring-secondary-500 sm:text-sm"
            >
              <option value="">Toutes les classes</option>
              <optgroup label="Collège">
                {niveaux.Collège.map((niveau) => (
                  <option key={niveau} value={niveau}>{niveau}</option>
                ))}
              </optgroup>
              <optgroup label="Lycée">
                {niveaux.Lycée.map((niveau) => (
                  <option key={niveau} value={niveau}>{niveau}</option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="flex items-center">
            <Filter className="h-4 w-4 text-gray-400 mr-2" />
            <select
              id="matiere"
              value={filters.matiere}
              onChange={(e) => {
                const newMatiere = e.target.value;
                if (newMatiere === 'Philosophie' && filters.niveau !== 'Terminale') {
                  setFilters({ ...filters, matiere: newMatiere, niveau: 'Terminale' });
                } else if (!isSubjectAvailable(newMatiere, filters.niveau)) {
                  setFilters({ ...filters, matiere: newMatiere, niveau: '' });
                } else {
                  setFilters({ ...filters, matiere: newMatiere });
                }
                setOwnFichesPage(1);
                setCommunityFichesPage(1);
              }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-secondary-500 focus:ring-secondary-500 sm:text-sm"
            >
              <option value="">Toutes les matières</option>
              {availableRegularMatieres.length > 0 && (
                <optgroup label="Matières générales">
                  {availableRegularMatieres.map((matiere) => (
                    <option key={matiere} value={matiere}>{matiere}</option>
                  ))}
                </optgroup>
              )}
              {availableSpecialtyMatieres.length > 0 && (
                <optgroup label="Spécialités">
                  {availableSpecialtyMatieres.map((matiere) => (
                    <option key={matiere} value={matiere}>{matiere}</option>
                  ))}
                </optgroup>
              )}
              {availableOptionalMatieres.length > 0 && (
                <optgroup label="Options">
                  {availableOptionalMatieres.map((matiere) => (
                    <option key={matiere} value={matiere}>{matiere}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div className="flex items-center">
            <input
              id="withQuiz"
              type="checkbox"
              checked={filters.withQuiz}
              onChange={(e) => {
                setFilters({ ...filters, withQuiz: e.target.checked });
                setOwnFichesPage(1);
                setCommunityFichesPage(1);
              }}
              className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
            />
            <label htmlFor="withQuiz" className="ml-2 block text-sm text-gray-900">
              Avec quiz uniquement
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-8 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-secondary-600 flex items-center">
            <FolderOpen className="h-5 w-5 mr-2" />
            Les fiches que j'ai créées
          </h2>
        </div>
        {ownFiches.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-500">Vous n'avez pas encore créé de fiches.</p>
            <Link
              to="/nouvelle-fiche"
              className="mt-4 inline-flex items-center text-sm text-secondary-600 hover:text-secondary-700"
            >
              Créer ma première fiche
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        ) : (
          <>
            <FichesList 
              fiches={ownFiches} 
              onToggleVisibility={handleToggleVisibility}
              isUpdatingVisibility={isUpdatingVisibility}
            />
            <Pagination
              currentPage={ownFichesPage}
              totalPages={totalOwnPages}
              onPageChange={setOwnFichesPage}
            />
          </>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-accent-600 flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Les fiches partagées par la communauté
          </h2>
        </div>
        {communityFiches.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-500">Aucune fiche partagée par la communauté pour le moment.</p>
          </div>
        ) : (
          <>
            <FichesList fiches={communityFiches} isShared={true} />
            <Pagination
              currentPage={communityFichesPage}
              totalPages={totalCommunityPages}
              onPageChange={setCommunityFichesPage}
            />
          
          </>
        )}
      </div>
    </div>
  );
};

export default MesFichesPage;